import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { auth } from "../../firebase";
import { useAuth } from "../../AuthContext";
import GamingSitesImport from "./GamingSitesImport.jsx";

// Admin-only: load every bar in the chosen cities, plus whatever specials
// and events each bar posts on its OWN website. Runs on the server
// (importAreaBars Cloud Function). Safe to run again: nothing duplicates,
// and bars already claimed by an owner or charity are left alone.

const FUNCTION_URL = "https://us-central1-bar-buzz-a6645.cloudfunctions.net/importAreaBars";
const BATCH_SIZE = 4;

const CITIES = [
  "Saint Paul", "Oakdale", "Maplewood", "North St. Paul", "Woodbury", "Roseville",
  "White Bear Lake", "Little Canada", "Vadnais Heights", "Shoreview", "New Brighton",
  "Arden Hills", "West St. Paul", "South St. Paul", "Inver Grove Heights",
  "Cottage Grove", "Lake Elmo", "Stillwater", "Mahtomedi", "Falcon Heights",
];

const STATUS_COLOR = {
  ok: "#4ADE80",
  "no website": "#B8B0C8",
  "claimed (left alone)": "#B9A6FF",
};

async function callImport(body) {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(FUNCTION_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

const box = { background: "#17131F", borderRadius: "14px", padding: "18px", marginBottom: "16px" };
const btn = {
  minHeight: "44px", padding: "0 18px", borderRadius: "10px", border: "none",
  font: "700 15px Inter, sans-serif", cursor: "pointer",
};

export default function AdminImport() {
  const { role } = useAuth();
  const [picked, setPicked] = useState(() => new Set(["Saint Paul", "Oakdale", "Maplewood", "North St. Paul"]));
  const [extraCity, setExtraCity] = useState("");
  const [bars, setBars] = useState([]); // found bars
  const [skipInApp, setSkipInApp] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | finding | found | loading | done
  const [findNote, setFindNote] = useState("");
  const [results, setResults] = useState([]);
  const [error, setError] = useState("");
  const stopRef = useRef(false);

  if (role !== "admin") return <p style={{ color: "#fff" }}>Admins only.</p>;

  const toggleCity = (c) =>
    setPicked((prev) => {
      const next = new Set(prev);
      next.has(c) ? next.delete(c) : next.add(c);
      return next;
    });

  const addCity = () => {
    const c = extraCity.trim();
    if (!c) return;
    setPicked((prev) => new Set(prev).add(c));
    setExtraCity("");
  };

  const findBars = async () => {
    setError("");
    setResults([]);
    setBars([]);
    setPhase("finding");
    const all = new Map();
    const cities = [...picked];
    try {
      for (let i = 0; i < cities.length; i++) {
        setFindNote(`Searching ${cities[i]} (${i + 1} of ${cities.length})…`);
        const data = await callImport({ action: "find", city: cities[i] });
        (data.bars || []).forEach((b) => {
          if (!all.has(b.placeId)) all.set(b.placeId, { ...b, city: cities[i] });
        });
        setBars([...all.values()]);
      }
      setFindNote("");
      setPhase("found");
    } catch (e) {
      setError(e.message);
      setPhase(all.size ? "found" : "idle");
    }
  };

  const toLoad = bars.filter((b) => !b.claimed && !(skipInApp && b.inApp));

  const loadAll = async () => {
    setError("");
    setResults([]);
    stopRef.current = false;
    setPhase("loading");
    for (let i = 0; i < toLoad.length; i += BATCH_SIZE) {
      if (stopRef.current) break;
      const batch = toLoad.slice(i, i + BATCH_SIZE).map((b) => b.placeId);
      try {
        const data = await callImport({ action: "load", placeIds: batch });
        setResults((prev) => [...prev, ...(data.results || [])]);
      } catch (e) {
        setResults((prev) => [
          ...prev,
          ...batch.map((id) => ({ placeId: id, name: bars.find((b) => b.placeId === id)?.name || id, status: `error: ${e.message}` })),
        ]);
      }
    }
    setPhase("done");
  };

  const totals = results.reduce(
    (t, r) => ({
      bars: t.bars + 1,
      withInfo: t.withInfo + ((r.specials || 0) + (r.events || 0) > 0 ? 1 : 0),
      specials: t.specials + (r.specials || 0),
      events: t.events + (r.events || 0),
    }),
    { bars: 0, withInfo: 0, specials: 0, events: 0 }
  );

  return (
    <div style={{ color: "#fff", maxWidth: "960px" }}>
      <h2 style={{ margin: "0 0 6px" }}>Load bars</h2>
      <p style={{ color: "#B8B0C8", margin: "0 0 18px", fontSize: "16px", lineHeight: 1.5 }}>
        Finds every bar Google lists in the cities you pick, then reads each bar's own website for specials and
        events. Everything goes live tagged "From their website" until the owner claims the bar. Bars already
        claimed are never changed. Safe to run again. Nothing gets doubled.
      </p>

      <div style={box}>
        <h3 style={{ margin: "0 0 12px", fontSize: "18px" }}>1. Pick cities</h3>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "12px" }}>
          {[...new Set([...CITIES, ...picked])].map((c) => {
            const on = picked.has(c);
            return (
              <button
                key={c}
                type="button"
                onClick={() => toggleCity(c)}
                aria-pressed={on}
                style={{
                  minHeight: "40px", padding: "0 14px", borderRadius: "20px", cursor: "pointer",
                  border: on ? "none" : "1px solid #3A3350",
                  background: on ? "#FF3D81" : "transparent",
                  color: on ? "#0B0B12" : "#fff",
                  font: `${on ? 700 : 500} 15px Inter, sans-serif`,
                }}
              >
                {c}
              </button>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <label htmlFor="extra-city" style={{ position: "absolute", left: "-9999px" }}>Add another city</label>
          <input
            id="extra-city"
            value={extraCity}
            onChange={(e) => setExtraCity(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addCity()}
            placeholder="Add another Minnesota city"
            style={{ flex: "1 1 240px", minHeight: "44px", padding: "0 12px", borderRadius: "10px", border: "1px solid #3A3350", background: "#0D0B1A", color: "#fff", fontSize: "15px" }}
          />
          <button type="button" onClick={addCity} style={{ ...btn, background: "#2A2440", color: "#fff" }}>Add</button>
        </div>
        <div style={{ marginTop: "16px" }}>
          <button
            type="button"
            onClick={findBars}
            disabled={phase === "finding" || phase === "loading" || picked.size === 0}
            style={{ ...btn, background: "#8B5CF6", color: "#fff", opacity: phase === "finding" || phase === "loading" ? 0.6 : 1 }}
          >
            {phase === "finding" ? "Searching…" : `Find bars in ${picked.size} ${picked.size === 1 ? "city" : "cities"}`}
          </button>
          {findNote && <span style={{ marginLeft: "12px", color: "#B8B0C8" }}>{findNote}</span>}
        </div>
      </div>

      {error && <p style={{ color: "#FF6B8A", fontSize: "16px" }}>{error}</p>}

      {bars.length > 0 && (
        <div style={box}>
          <h3 style={{ margin: "0 0 8px", fontSize: "18px" }}>2. Load them</h3>
          <p style={{ margin: "0 0 12px", color: "#B8B0C8" }}>
            Found <strong style={{ color: "#fff" }}>{bars.length}</strong> bars ·{" "}
            {bars.filter((b) => b.inApp).length} already in the app · {bars.filter((b) => b.claimed).length} claimed
            by owners (skipped)
          </p>
          <label style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "14px", cursor: "pointer" }}>
            <input type="checkbox" checked={skipInApp} onChange={(e) => setSkipInApp(e.target.checked)} style={{ width: "18px", height: "18px" }} />
            Skip bars already in the app
          </label>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
            <button
              type="button"
              onClick={loadAll}
              disabled={phase === "loading" || toLoad.length === 0}
              style={{ ...btn, background: "#FF3D81", color: "#0B0B12", opacity: phase === "loading" ? 0.6 : 1 }}
            >
              {phase === "loading" ? "Loading…" : `Load ${toLoad.length} bars`}
            </button>
            {phase === "loading" && (
              <>
                <span style={{ color: "#B8B0C8" }}>{results.length} of {toLoad.length} done. Keep this tab open.</span>
                <button type="button" onClick={() => { stopRef.current = true; }} style={{ ...btn, background: "transparent", border: "1px solid #3A3350", color: "#fff" }}>
                  Stop
                </button>
              </>
            )}
          </div>

          {phase === "loading" && toLoad.length > 0 && (
            <div style={{ marginTop: "12px", height: "8px", borderRadius: "4px", background: "#0D0B1A", overflow: "hidden" }}>
              <div style={{ width: `${Math.round((results.length / toLoad.length) * 100)}%`, height: "100%", background: "#FF3D81" }} />
            </div>
          )}

          {results.length === 0 && phase !== "loading" && (
            <ul style={{ margin: "14px 0 0", paddingLeft: "18px", color: "#D6D2E4", maxHeight: "300px", overflowY: "auto", lineHeight: 1.6 }}>
              {bars.map((b) => (
                <li key={b.placeId}>
                  {b.name} <span style={{ color: "#8A84A0" }}>· {b.address}</span>
                  {b.claimed ? <span style={{ color: "#B9A6FF" }}> · claimed</span> : b.inApp ? <span style={{ color: "#8A84A0" }}> · in app</span> : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {results.length > 0 && (
        <div style={box}>
          <h3 style={{ margin: "0 0 8px", fontSize: "18px" }}>Results</h3>
          <p style={{ margin: "0 0 12px", color: "#D6D2E4" }}>
            {totals.bars} bars loaded · {totals.withInfo} had specials or events online · {totals.specials} specials ·{" "}
            {totals.events} events
          </p>
          <div style={{ maxHeight: "420px", overflowY: "auto", fontSize: "15px", lineHeight: 1.7 }}>
            {results.map((r) => (
              <div key={r.placeId}>
                <strong>{r.name}</strong>{" "}
                <span style={{ color: STATUS_COLOR[r.status] || "#FFB86B" }}>
                  {r.status === "ok"
                    ? `· ${r.specials} specials, ${r.events} events${r.amenities ? `, ${r.amenities} amenities` : ""}`
                    : `· ${r.status}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <GamingSitesImport />

      <p style={{ color: "#8A84A0", fontSize: "14px" }}>
        Older hand-checked Oakdale list (includes bingo times from mnbarbingo.com):{" "}
        <Link to="/admin/import-oakdale" style={{ color: "#B9A6FF" }}>open it</Link>
      </p>
    </div>
  );
}
